import React, { useEffect, useMemo, useState } from "react";
import { Heart, Activity, AlertTriangle, FileText, Stethoscope, ShieldCheck, Sparkles, Loader2, Download, Pill } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AiAnswerCard from "@/components/AiAnswerCard";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

const SUBS = [
  { key: "acs",    label: "CARD-01 · ACS Risk", icon: Activity },
  { key: "hf",     label: "CARD-02 · Heart Failure", icon: Heart },
  { key: "afib",   label: "CARD-03 · Atrial Fibrillation", icon: Activity },
  { key: "htn",    label: "CARD-04 · Hypertension", icon: ShieldCheck },
  { key: "lipids", label: "CARD-05 · Lipids / ASCVD", icon: Pill },
  { key: "doc",    label: "CARD-06 · Documentation", icon: FileText },
  { key: "preop",  label: "CARD-07 · Pre-Op Clearance", icon: Stethoscope },
];

// ACC/AHA Class color coding: I green | IIa gold | IIb amber | III red
const CLASS_STYLE = {
  "I":   "bg-emerald-50 text-emerald-800 border-emerald-300",
  "IIa": "bg-amber-50 text-amber-900 border-amber-400",
  "IIb": "bg-orange-50 text-orange-800 border-orange-300",
  "III": "bg-red-50 text-red-800 border-red-300",
};

// Navy/gold palette tokens for the cardiology surface
const NAVY = "#0E1B3A";
const GOLD = "#C9A24B";

export default function Cardiology() {
  const [tab, setTab] = useState("acs");
  const Active = SUBS.find(s => s.key === tab);
  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><Heart className="w-5 h-5" style={{color: GOLD}} /> Cardiology</span>}
        subtitle="ACC/AHA-aligned risk stratification, GDMT, and documentation. Every recommendation cites Class & Level of Evidence."
        testid="cardio-header" />

      <div className="rounded-lg border mb-5 overflow-hidden" style={{borderColor: NAVY, background: NAVY}} data-testid="cardio-tabs">
        <div className="flex flex-wrap">
          {SUBS.map(s => {
            const Ic = s.icon;
            const active = s.key === tab;
            return (
              <button key={s.key} onClick={() => setTab(s.key)}
                className={`flex-1 min-w-[140px] px-3 py-2.5 text-xs font-medium inline-flex items-center justify-center gap-2 transition-colors border-b-2`}
                style={{
                  color: active ? GOLD : "#cbd5e1",
                  borderColor: active ? GOLD : "transparent",
                  background: active ? "rgba(201,162,75,0.08)" : "transparent",
                }}
                data-testid={`cardio-tab-${s.key}`}>
                <Ic className="w-3.5 h-3.5" /> {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === "acs"    && <ACSPanel />}
      {tab === "hf"     && <HFPanel />}
      {tab === "afib"   && <AFibPanel />}
      {tab === "htn"    && <HTNPanel />}
      {tab === "lipids" && <LipidsPanel />}
      {tab === "doc"    && <DocPanel />}
      {tab === "preop"  && <PreopPanel />}
    </>
  );
}

// ---------- Shared output rendering ----------
function OutputCard({ result, title, citationKey, testid, children }) {
  if (!result) return null;
  const urgent = result.urgent_flags || [];
  return (
    <div className="space-y-3" data-testid={testid}>
      {urgent.length > 0 && (
        <div className="rounded-md border-2 border-red-500 bg-red-50 p-3 text-sm" data-testid={`${testid}-urgent`}>
          <div className="flex items-center gap-2 font-semibold text-red-800 mb-1">
            <AlertTriangle className="w-4 h-4" /> URGENT
          </div>
          <ul className="space-y-0.5 text-red-900">
            {urgent.map((u, i) => <li key={i}>• {u}</li>)}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border" style={{background: NAVY, color: GOLD, borderColor: NAVY}}>
          {result.guideline_badge}
        </span>
        {result.evidence_class && (
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${CLASS_STYLE[result.evidence_class] || "bg-gray-100 border-gray-300"}`} data-testid={`${testid}-class`}>
            Class {result.evidence_class} · LOE {result.level_of_evidence}
          </span>
        )}
      </div>
      <SectionCard title={title} testid={`${testid}-card`}>
        {children}
        <div className="mt-4 pt-3 border-t border-brand text-[11px] text-brand-muted italic" data-testid={`${testid}-citation`}>
          <strong className="not-italic">Citation:</strong> {result.citation}
        </div>
        <div className="mt-2 text-[11px] text-amber-800 italic">{result.disclaimer}</div>
      </SectionCard>
    </div>
  );
}

const InputRow = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center py-1">
    <label className="text-xs text-brand-muted">{label}</label>
    {children}
  </div>
);
const Num = (props) => <input type="number" className="w-full sm:w-32 px-2 py-1.5 border border-brand rounded-md text-base bg-white" {...props} />;
const Chk = (props) => <input type="checkbox" className="w-4 h-4" {...props} />;
const Sel = ({ options, ...props }) => (
  <select className="w-full sm:w-40 px-2 py-1.5 border border-brand rounded-md text-base bg-white" {...props}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const CategoryPill = ({ value }) => {
  const map = { low: "bg-emerald-50 text-emerald-700 border-emerald-200",
                intermediate: "bg-amber-50 text-amber-800 border-amber-300",
                borderline: "bg-amber-50 text-amber-800 border-amber-300",
                high: "bg-red-50 text-red-700 border-red-300",
                elevated: "bg-red-50 text-red-700 border-red-300" };
  return <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase font-bold ${map[value] || "bg-gray-50 border-gray-200"}`}>{value}</span>;
};

// ---------- Per-panel debounce helper ----------
function useDebouncedPost(path, payload, deps, validate) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (validate && !validate()) { setData(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const r = await api.post(path, payload); setData(r.data); }
      catch { setData(null); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [data, loading];
}

// ---------- CARD-01 ACS ----------
function ACSPanel() {
  const [s, set] = useState({ age: 65, ge3_risk_factors: false, known_cad_50pct: false, aspirin_in_7d: false,
    severe_angina_24h: false, st_deviation: false, positive_marker: false,
    heart_history_pts: 1, heart_ecg_pts: 1, heart_age_pts: 1, heart_risk_factors_pts: 1, heart_troponin_pts: 1,
    hr: 80, sbp: 130, creatinine: 1.0, killip_class: 1, cardiac_arrest: false });
  const [data] = useDebouncedPost("/cardiology/acs-risk", s, [JSON.stringify(s)], () => s.age > 0);
  const u = (k) => (e) => set({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : (e.target.type === "number" ? Number(e.target.value) : e.target.value) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="Clinical Inputs" testid="acs-inputs">
        <InputRow label="Age"><Num value={s.age} onChange={u("age")} /></InputRow>
        <InputRow label="Heart rate"><Num value={s.hr} onChange={u("hr")} /></InputRow>
        <InputRow label="SBP"><Num value={s.sbp} onChange={u("sbp")} /></InputRow>
        <InputRow label="Creatinine"><Num step="0.1" value={s.creatinine} onChange={u("creatinine")} /></InputRow>
        <InputRow label="Killip class (1-4)"><Sel value={s.killip_class} onChange={u("killip_class")} options={[1,2,3,4].map(v=>({value:v,label:String(v)}))} /></InputRow>
        <div className="mt-3 pt-3 border-t border-brand grid grid-cols-1 gap-1.5 text-xs">
          {[
            ["ge3_risk_factors", "≥3 CAD risk factors"],
            ["known_cad_50pct", "Known CAD (≥50% stenosis)"],
            ["aspirin_in_7d", "Aspirin use in last 7 days"],
            ["severe_angina_24h", "Severe angina in last 24h"],
            ["st_deviation", "ST deviation on ECG"],
            ["positive_marker", "Positive cardiac biomarker"],
            ["cardiac_arrest", "Cardiac arrest at presentation"],
          ].map(([k,l]) => (
            <label key={k} className="flex items-center gap-2"><Chk checked={s[k]} onChange={u(k)} data-testid={`acs-${k}`} /> {l}</label>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-brand text-[11px] uppercase tracking-wider text-brand-muted mb-1">HEART score components (0/1/2)</div>
        {[["heart_history_pts","History"],["heart_ecg_pts","ECG"],["heart_age_pts","Age"],["heart_risk_factors_pts","Risk factors"],["heart_troponin_pts","Troponin"]].map(([k,l])=>(
          <InputRow key={k} label={l}><Sel value={s[k]} onChange={u(k)} options={[0,1,2].map(v=>({value:v,label:String(v)}))} /></InputRow>
        ))}
      </SectionCard>
      <OutputCard result={data} title="Risk Stratification & Pathway" citationKey="acs" testid="acs-out">
        {data && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <KpiCard label="TIMI" value={data.timi.score} hint={`14d MACE ${data.timi.fourteen_day_event_pct}%`} testid="acs-timi" />
              <KpiCard label="HEART" value={data.heart.score} hint={`6wk MACE ${data.heart.six_week_mace_pct_range}`} testid="acs-heart" />
              <KpiCard label="GRACE" value={data.grace.score} hint={`In-hosp mort ${data.grace.in_hospital_mortality_pct_range}`} testid="acs-grace" />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-brand-muted">Overall:</span> <CategoryPill value={data.overall_category} />
            </div>
            <div className="text-sm text-brand-text leading-relaxed bg-brand-bg rounded-md p-3" data-testid="acs-pathway">{data.pathway}</div>
            <CodingRow icd10={data.icd10} cpt={data.cpt} />
          </>
        )}
      </OutputCard>
    </div>
  );
}

const CodingRow = ({ icd10 = [], cpt = [] }) => (
  <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
    <span className="text-brand-muted">ICD-10:</span>
    {icd10.filter(Boolean).map(c => <span key={c} className="font-mono px-2 py-0.5 bg-brand-bg border border-brand rounded">{c}</span>)}
    <span className="text-brand-muted ml-2">CPT:</span>
    {cpt.filter(Boolean).map(c => <span key={c} className="font-mono px-2 py-0.5 bg-brand-bg border border-brand rounded">{c}</span>)}
  </div>
);

// ---------- CARD-02 HF ----------
function HFPanel() {
  const [s, set] = useState({ ef_pct: 35, nyha_class: 2, symptoms: true, structural_disease: true,
    hf_history: true, refractory: false, decompensated: false });
  const [data] = useDebouncedPost("/cardiology/hf", s, [JSON.stringify(s)], () => s.ef_pct >= 0);
  const u = (k) => (e) => set({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : Number(e.target.value) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="HF Inputs" testid="hf-inputs">
        <InputRow label="Ejection Fraction %"><Num value={s.ef_pct} onChange={u("ef_pct")} /></InputRow>
        <InputRow label="NYHA Class"><Sel value={s.nyha_class} onChange={u("nyha_class")} options={[1,2,3,4].map(v=>({value:v,label:`Class ${v}`}))} /></InputRow>
        <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs">
          {[["symptoms","Symptoms present"],["structural_disease","Structural heart disease"],["hf_history","Prior HF hospitalisation"],["refractory","Refractory / Stage D"],["decompensated","Acutely decompensated now"]].map(([k,l])=>(
            <label key={k} className="flex items-center gap-2"><Chk checked={s[k]} onChange={u(k)} data-testid={`hf-${k}`} /> {l}</label>
          ))}
        </div>
      </SectionCard>
      <OutputCard result={data} title="ACC/AHA Stage + GDMT" citationKey="hf" testid="hf-out">
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KpiCard label="Stage" value={data.stage} hint="ACC/AHA 2022" testid="hf-stage" />
              <KpiCard label="Category" value={data.category} hint={`NYHA ${data.nyha}`} testid="hf-category" />
            </div>
            <div className="text-xs uppercase tracking-wider text-brand-muted mb-2">GDMT Checklist</div>
            <ul className="space-y-1.5 text-sm" data-testid="hf-gdmt">
              {data.gdmt_checklist.map((g, i) => (
                <li key={i} className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">✓</span><span>{g}</span></li>
              ))}
            </ul>
            <CodingRow icd10={data.icd10} cpt={data.cpt} />
          </>
        )}
      </OutputCard>
    </div>
  );
}

// ---------- CARD-03 AFib ----------
function AFibPanel() {
  const [s, set] = useState({ age: 70, sex: "M", chf: false, htn: true, diabetes: false, stroke_tia: false,
    vascular_disease: false, htn_uncontrolled: false, renal_disease: false, liver_disease: false,
    bleed_history: false, labile_inr: false, drugs_or_alcohol: false, hr: 95, rapid_ventricular: false });
  const [data] = useDebouncedPost("/cardiology/afib", s, [JSON.stringify(s)], () => s.age > 0);
  const u = (k) => (e) => set({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : (e.target.type === "number" ? Number(e.target.value) : e.target.value) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="Risk Factors" testid="afib-inputs">
        <InputRow label="Age"><Num value={s.age} onChange={u("age")} /></InputRow>
        <InputRow label="Sex"><Sel value={s.sex} onChange={u("sex")} options={[{value:"M",label:"Male"},{value:"F",label:"Female"}]} /></InputRow>
        <InputRow label="Ventricular rate"><Num value={s.hr} onChange={u("hr")} /></InputRow>
        <div className="mt-2 text-[11px] uppercase tracking-wider text-brand-muted">CHA₂DS₂-VASc</div>
        {[["chf","CHF"],["htn","HTN"],["diabetes","Diabetes"],["stroke_tia","Prior stroke / TIA"],["vascular_disease","Vascular disease"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-2 text-xs py-0.5"><Chk checked={s[k]} onChange={u(k)} data-testid={`afib-${k}`} /> {l}</label>
        ))}
        <div className="mt-2 text-[11px] uppercase tracking-wider text-brand-muted">HAS-BLED</div>
        {[["htn_uncontrolled","Uncontrolled HTN"],["renal_disease","Renal disease"],["liver_disease","Liver disease"],["bleed_history","Bleed history"],["labile_inr","Labile INR"],["drugs_or_alcohol","Drugs / alcohol"],["rapid_ventricular","Rapid ventricular response"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-2 text-xs py-0.5"><Chk checked={s[k]} onChange={u(k)} /> {l}</label>
        ))}
      </SectionCard>
      <OutputCard result={data} title="Anticoagulation + Rate/Rhythm" citationKey="afib" testid="afib-out">
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KpiCard label="CHA₂DS₂-VASc" value={data.cha2ds2_vasc.score} hint={`Stroke risk ${data.cha2ds2_vasc.annual_stroke_risk_pct}%/yr`} testid="afib-cha" />
              <KpiCard label="HAS-BLED" value={data.has_bled.score} hint={`Bleed ${data.has_bled.annual_major_bleed_pct}%/yr`} testid="afib-hb" />
            </div>
            <div className="mb-2 text-sm bg-brand-bg rounded-md p-3" data-testid="afib-ac"><strong className="text-brand-primary">Anticoagulation:</strong> {data.anticoagulation_recommendation}</div>
            <div className="text-sm bg-brand-bg rounded-md p-3"><strong className="text-brand-primary">Rate vs Rhythm:</strong> {data.rate_vs_rhythm}</div>
            <CodingRow icd10={data.icd10} cpt={data.cpt} />
          </>
        )}
      </OutputCard>
    </div>
  );
}

// ---------- CARD-04 HTN ----------
function HTNPanel() {
  const [s, set] = useState({ sbp: 138, dbp: 88, ckd: false, diabetes: false, cad: false, hf: false, ascvd_risk_pct: 0 });
  const [data] = useDebouncedPost("/cardiology/htn", s, [JSON.stringify(s)], () => s.sbp > 0);
  const u = (k) => (e) => set({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : Number(e.target.value) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="BP & Comorbidities" testid="htn-inputs">
        <InputRow label="SBP"><Num value={s.sbp} onChange={u("sbp")} /></InputRow>
        <InputRow label="DBP"><Num value={s.dbp} onChange={u("dbp")} /></InputRow>
        <InputRow label="10-yr ASCVD %"><Num step="0.1" value={s.ascvd_risk_pct} onChange={u("ascvd_risk_pct")} /></InputRow>
        {[["ckd","CKD"],["diabetes","Diabetes"],["cad","CAD / post-MI"],["hf","Heart failure"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-2 text-xs py-0.5"><Chk checked={s[k]} onChange={u(k)} data-testid={`htn-${k}`} /> {l}</label>
        ))}
      </SectionCard>
      <OutputCard result={data} title="Stage & Drug Algorithm" citationKey="htn" testid="htn-out">
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KpiCard label="Stage" value={data.label} testid="htn-stage" />
              <KpiCard label="BP Target" value={data.bp_target} testid="htn-target" />
            </div>
            <div className="text-xs uppercase tracking-wider text-brand-muted mb-2">Drug Selection</div>
            <ul className="space-y-1.5 text-sm" data-testid="htn-drugs">
              {data.drug_algorithm.map((g, i) => <li key={i}>• {g}</li>)}
            </ul>
            <CodingRow icd10={data.icd10} cpt={data.cpt} />
          </>
        )}
      </OutputCard>
    </div>
  );
}

// ---------- CARD-05 Lipids ----------
function LipidsPanel() {
  const [s, set] = useState({ age: 55, sex: "M", race: "white", total_chol: 200, hdl: 45, ldl: 130,
    sbp: 135, treated_htn: false, diabetes: false, smoker: false, ascvd_history: false });
  const [data] = useDebouncedPost("/cardiology/lipids", s, [JSON.stringify(s)], () => s.age >= 40);
  const u = (k) => (e) => set({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : (e.target.type === "number" ? Number(e.target.value) : e.target.value) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="ASCVD Inputs (validated ages 40-79)" testid="lipids-inputs">
        <InputRow label="Age"><Num value={s.age} onChange={u("age")} /></InputRow>
        <InputRow label="Sex"><Sel value={s.sex} onChange={u("sex")} options={[{value:"M",label:"Male"},{value:"F",label:"Female"}]} /></InputRow>
        <InputRow label="Race"><Sel value={s.race} onChange={u("race")} options={[{value:"white",label:"White / Other"},{value:"black",label:"African American"}]} /></InputRow>
        <InputRow label="Total cholesterol"><Num value={s.total_chol} onChange={u("total_chol")} /></InputRow>
        <InputRow label="HDL"><Num value={s.hdl} onChange={u("hdl")} /></InputRow>
        <InputRow label="LDL"><Num value={s.ldl} onChange={u("ldl")} /></InputRow>
        <InputRow label="SBP"><Num value={s.sbp} onChange={u("sbp")} /></InputRow>
        {[["treated_htn","On BP medication"],["diabetes","Diabetes"],["smoker","Current smoker"],["ascvd_history","Established ASCVD"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-2 text-xs py-0.5"><Chk checked={s[k]} onChange={u(k)} data-testid={`lip-${k}`} /> {l}</label>
        ))}
      </SectionCard>
      <OutputCard result={data} title="ASCVD Risk + Statin Strategy" citationKey="lipids" testid="lipids-out">
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KpiCard label="10-yr ASCVD" value={data.ascvd_10yr.risk_pct != null ? `${data.ascvd_10yr.risk_pct}%` : "—"} hint={data.ascvd_10yr.category} testid="lip-risk" />
              <KpiCard label="Statin Intensity" value={data.statin.intensity} testid="lip-statin" />
            </div>
            <div className="text-sm bg-brand-bg rounded-md p-3 mb-2" data-testid="lip-rec">{data.statin.rationale}</div>
            {data.statin.ldl_target && <div className="text-xs text-brand-muted">LDL target: <strong>{data.statin.ldl_target}</strong></div>}
            {data.statin.pcsk9_eligible && <div className="text-xs text-amber-700 mt-1">⚑ PCSK9 inhibitor eligible if LDL not at target on max statin</div>}
            <CodingRow icd10={data.icd10} cpt={data.cpt} />
          </>
        )}
      </OutputCard>
    </div>
  );
}

// ---------- CARD-06 Documentation (AI) ----------
function DocPanel() {
  const [scenario, setScenario] = useState("soap");
  const [form, setForm] = useState({ chief_complaint: "Chest pain", hpi: "65 y/o M with substernal pressure x 2h, radiating to left arm.",
    findings: "BP 138/86, HR 90, ECG: TWI V4-V6. Trop I 0.08.", diagnosis: "Non-ST elevation ACS",
    plan: "Admit telemetry, aspirin 325, ticagrelor LD, heparin, statin, cardiology consult.", referring_provider: "Dr. Smith" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const d = await aiJob("/cardiology/documentation", { scenario, ...form });
      setResult(d);
      toast.success("Documentation drafted by Topology AI");
    } catch { toast.error("Generation failed"); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="Documentation Inputs" testid="doc-inputs"
        actions={result && <button onClick={() => exportToPDF("Cardiac Documentation", result.final)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />PDF</button>}>
        <InputRow label="Scenario"><Sel value={scenario} onChange={(e)=>setScenario(e.target.value)} options={[{value:"soap",label:"SOAP note"},{value:"discharge",label:"Discharge summary"},{value:"consult",label:"Consult letter"}]} /></InputRow>
        {[["chief_complaint","Chief complaint"],["hpi","HPI"],["findings","Exam / labs / studies"],["diagnosis","Diagnosis"],["plan","Plan"],["referring_provider","Referring provider"]].map(([k,l])=>(
          <div key={k} className="py-1">
            <label className="text-xs text-brand-muted block mb-1">{l}</label>
            <textarea rows={k==="hpi"||k==="findings"||k==="plan"?3:1} value={form[k]||""} onChange={(e)=>setForm({...form,[k]:e.target.value})}
              className="w-full px-2 py-1.5 border border-brand rounded-md text-base bg-white" data-testid={`doc-${k}`} />
          </div>
        ))}
        <button onClick={generate} disabled={busy} className="mt-3 w-full bg-brand-primary text-white py-2.5 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="doc-generate">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
        </button>
      </SectionCard>
      <SectionCard title="ACC/AHA-compliant Documentation" testid="doc-out">
        {!result && !busy && <div className="text-sm text-brand-muted">Fill in inputs and click Generate.</div>}
        {busy && <div className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin" />Drafting…</div>}
        {result && (
          <>
            <AIOutputDisclaimer />
            <pre className="text-sm whitespace-pre-wrap leading-relaxed text-brand-text max-h-96 overflow-y-auto" data-testid="doc-text">{result.final}</pre>
            {result.verification_footer && (
              <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="doc-verify">{result.verification_footer}</div>
            )}
            <ShowReasoning reasoning={result.reasoning} score={result.score} testid="doc-reasoning" />
            <AiAnswerCard
              platformModule="cardiology"
              originalQuery="Cardiology recommendation drafting"
              originalAiResponse={result.final}
              retrievedEntryIds={result.retrieved_entry_ids || []}
              suggestedDomain="cardiology"
              testid="cardio-answer-card"
            />
          </>
        )}
      </SectionCard>
    </div>
  );
}

// ---------- CARD-07 Pre-op ----------
function PreopPanel() {
  const [s, set] = useState({ high_risk_surgery: false, ischemic_heart_disease: false, hf_history: false,
    cerebrovascular_disease: false, insulin_diabetes: false, creatinine_over_2: false, mets: 4, active_cardiac_condition: false });
  const [data] = useDebouncedPost("/cardiology/preop", s, [JSON.stringify(s)], () => true);
  const u = (k) => (e) => set({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : Number(e.target.value) });
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <SectionCard title="RCRI Components" testid="preop-inputs">
        <InputRow label="Functional capacity (METs)"><Num step="0.5" value={s.mets} onChange={u("mets")} /></InputRow>
        {[["high_risk_surgery","High-risk surgery (vascular/intra-thoracic/intra-peritoneal)"],
          ["ischemic_heart_disease","Ischemic heart disease"],
          ["hf_history","Heart failure history"],
          ["cerebrovascular_disease","Cerebrovascular disease (stroke/TIA)"],
          ["insulin_diabetes","Insulin-treated diabetes"],
          ["creatinine_over_2","Creatinine > 2.0 mg/dL"],
          ["active_cardiac_condition","ACTIVE cardiac condition"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-2 text-xs py-0.5"><Chk checked={s[k]} onChange={u(k)} data-testid={`preop-${k}`} /> {l}</label>
        ))}
      </SectionCard>
      <OutputCard result={data} title="Perioperative Recommendation" citationKey="preop" testid="preop-out">
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <KpiCard label="RCRI" value={data.rcri.score} hint={`MACE ${data.rcri.mace_pct}%`} testid="preop-rcri" />
              <KpiCard label="Category" value={data.rcri.category} testid="preop-cat" />
            </div>
            <div className="text-sm bg-brand-bg rounded-md p-3 mb-2" data-testid="preop-rec">{data.recommendation}</div>
            <div className="text-xs text-brand-muted">
              Stress test: <strong>{data.stress_test ? "Consider" : "Not indicated"}</strong> · Cardiology consult: <strong>{data.cardiology_consult ? "Advised" : "Not required"}</strong>
            </div>
            <CodingRow icd10={data.icd10} cpt={data.cpt} />
          </>
        )}
      </OutputCard>
    </div>
  );
}
